import React from 'react';
import { observable, autorun, action } from 'mobx';
import { FormattedMessage } from 'react-intl';
import { observer } from 'mobx-react';
import PropTypes from 'prop-types';
import classnames from 'classnames';

import ScrollToTop from 'lib/scroll/scroll-to-top';
import Styled from 'styled';
import { ForumRectangle, ZahradaBoxin, ZoznamBoxin } from 'lib/google/dfp/ad-units/forum-rectangle';
import { Targetting } from 'lib/google/dfp/google-dfp';
import { InterscrollerPlaceholder } from 'lib/google/dfp/ad-units/interscroller';
import SVGIconView from 'lib/images/svg-icon-view';
import { AuthOrigin } from 'four-nets/user/auth/auth-ga-events';
import { ForumCategoryStrip } from 'lib/google/dfp/ad-units/forum-category-strip';

import newArticle from 'four-nets/icons/svg/icon_new-article.svg';
import LoadingWrapperView from 'four-nets/ui/loaders/loading-wrapper-view';
import ActionButtonView from 'four-nets/ui/action-button/action-button-view';
import notification from 'four-nets/icons/svg/icon_notifications.svg';
import BigButtonView from 'four-nets/ui/big-button/big-button-view';
import Pager from 'four-nets/ui-v2/pager/page-number-pager';

import { ViewerConsumer } from 'four-nets/user/viewer/viewer';

import MessageView, { MessageViewModel } from '../../message/message-view';
import NewMessageView, { NewMessageViewModel } from '../../message/new-message-view';
import defaultStyles from './thread-messages-view.scss';

export class ThreadMessagesViewModel {
    thread: Thread;
    newMessageViewModel: NewMessageViewModel;

    @observable messageViewModels: MessageViewModel[] = [];
    @observable descending = false;
    @observable loading = false;
    @observable scrollToId;

    disposer: Function;

    @action setDescending(desc) {
        this.descending = desc;
    }

    constructor(options) {
        const { thread, mailStore, newMessageViewModel, urlBuilder } = options;

        this.thread = thread;
        this.newMessageViewModel = newMessageViewModel;
        this.urlBuilder = urlBuilder;
        this.mailStore = mailStore;

        this.disposer = autorun(() => {
            this.messageViewModels.replace(
                thread.messages.results.map(
                    message =>
                        new MessageViewModel({
                            urlBuilder,
                            message,
                            userMentionable: newMessageViewModel,
                            mailStore,
                        })
                )
            );
        });
    }

    messagesToViewModels = messages => {
        return messages.results.map(
            message =>
                new MessageViewModel({
                    message,
                    urlBuilder: this.urlBuilder,
                    userMentionable: this.newMessageViewModel,
                    mailStore: this.mailStore,
                })
        );
    };

    fromJSON(json) {
        this.scrollToId = json.scrollToId;
        this.descending = json.descending;
    }

    toJSON() {
        return {
            scrollToId: this.scrollToId,
            descending: this.descending,
        };
    }
}

@Styled(defaultStyles)
@observer
class ThreadMessagesView extends React.Component {
    static contextTypes = {
        scrollView: PropTypes.object,
        authCheck: PropTypes.func,
        site: PropTypes.object,
    };

    ref: ?HTMLDivElement;
    messageRef = {};
    toggleNotificationsPromise: ?Promise<any>;
    state = { stripAdVisible: false };

    state = { focus: false };

    setRef = (ref: ?HTMLDivElement) => {
        this.ref = ref;
    };

    scrollToNewMessage = () => {
        this.setState({ focus: true });
        this.context.scrollView.scrollToRef({
            ref: this.ref,
            offset: -70,
        });
    };

    setMessageRef = ({ id }) => ref => {
        this.messageRef[id] = ref;
    };

    toggleNotifications = () => {
        this.toggleNotificationsPromise = this.context.authCheck(
            this.props.threadMessagesViewModel.thread.toggleNotifications,
            AuthOrigin.Notifications
        );
        return this.toggleNotificationsPromise;
    };

    componentDidMount() {
        const ref = this.messageRef[this.props.threadMessagesViewModel.scrollToId];
        if (ref) {
            setTimeout(() => {
                this.context.scrollView.scrollToRef({
                    ref,
                    offset: -90,
                });
            }, 300);
        }
    }

    componentDidUpdate(props) {
        const ref = this.messageRef[this.props.post];

        if (ref && this.props.post !== props.post) {
            setTimeout(() => {
                this.context.scrollView.scrollToRef({
                    ref,
                    offset: -90,
                });
            }, 300);
        }
    }

    componentWillUnmount() {
        this.props.threadMessagesViewModel.disposer();

        if (this.toggleNotificationsPromise) {
            this.toggleNotificationsPromise.cancel();
        }
    }

    query = query => {
        const { post, ...rest } = query;
        return rest;
    };

    squareAd = position => {
        const { threadMessagesViewModel, styles } = this.props;
        const { thread } = threadMessagesViewModel;
        const { site } = this.context;

        return (
            <li key={`rectangle-${position}`} className={styles.squareAd}>
                {(() => {
                    if (site.isWedding && site.isSlovak) {
                        return <ZoznamBoxin position={position} slotRenderEndedCb={this.slotRenderEndedCb} />
                    }
                    
                    if (site.isGarden) {
                        return <ZahradaBoxin slotRenderEndedCb={this.slotRenderEndedCb} />
                    }

                    return (
                        <ForumRectangle
                            adUnitCode={`forum-rectangle-${position}`}
                            slotRenderEndedCb={this.slotRenderEndedCb}
                            targettings={[new Targetting('f-rectangle-cat-slug', [thread.category.slug])]}
                        />
                    )
                })()}
            </li>
        );
    };

    stripSlotRenderEndedCb = event => {
        this.setState({ stripAdVisible: !event.isEmpty });
    };

    render() {
        const { threadMessagesViewModel, messages, styles } = this.props;
        const { thread, newMessageViewModel } = threadMessagesViewModel;
        const { stripAdVisible } = this.state;

        const messageViewModels = threadMessagesViewModel.messagesToViewModels(messages);

        return (
            <ViewerConsumer>
                {({ viewer }) => (
                    <div className={styles.threadMessages}>
                        <div className={styles.header}>
                            <div className={styles.buttons}>
                                {thread.isLocked ? null : (
                                    <FormattedMessage id='SEND_REPLY' values={{ gender: 'm' }}>
                                        {msg => (
                                            <div className={styles.replyButton}>
                                                <BigButtonView
                                                    icon={newArticle}
                                                    onClick={this.scrollToNewMessage}
                                                    label={msg}
                                                />
                                            </div>
                                        )}
                                    </FormattedMessage>
                                )}
                                <span className={styles.notificationsButton}>
                                    <ActionButtonView
                                        icon={<SVGIconView src={notification} />}
                                        onClick={this.toggleNotifications}
                                    >
                                        {thread.subscribed ? (
                                            <FormattedMessage id='FORUM_THREAD_UNSUBSCRIBE' />
                                        ) : (
                                            <FormattedMessage id='FORUM_THREAD_SUBSCRIBE' />
                                        )}
                                    </ActionButtonView>
                                </span>
                            </div>
                            {thread.messages.pages > 1 ? (
                                <div className={styles.controls}>
                                    <Pager currentPage={thread.messages.page} pageCount={thread.messages.pages} />
                                </div>
                            ) : null}
                        </div>
                        <ScrollToTop page={thread.messages.page} />
                        <LoadingWrapperView loading={threadMessagesViewModel.loading}>
                            <ul className={styles.messages}>
                                {messageViewModels.map((messageViewModel, index) => {
                                    const element =
                                        messageViewModel.message.isPurged &&
                                        !(thread.userStore.me && thread.userStore.me.isSuperstaff) ? null : (
                                            <li
                                                ref={this.setMessageRef(messageViewModel.message)}
                                                key={messageViewModel.message.id}
                                                className={classnames(styles.message, {
                                                    [styles.noPadding]: stripAdVisible && index === 9,
                                                })}
                                            >
                                                <MessageView
                                                    key={messageViewModel.message.id}
                                                    messageViewModel={messageViewModel}
                                                />
                                            </li>
                                        );

                                    if (index === 3 && messageViewModels.length > 4) {
                                        return (
                                            <React.Fragment key={messageViewModel.message.id}>
                                                {this.squareAd('top')}
                                                <InterscrollerPlaceholder
                                                    style={{ marginBottom: 10 }}
                                                    targettings={[
                                                        new Targetting('f-rectangle-cat-slug', [thread.category.slug]),
                                                    ]}
                                                />
                                                {element}
                                            </React.Fragment>
                                        );
                                    } else if (index === 10) {
                                        const targettings = thread.category
                                            ? [new Targetting('f-rectangle-cat-slug', [thread.category.slug])]
                                            : [];
                                        return (
                                            <React.Fragment>
                                                <ForumCategoryStrip
                                                    targettings={targettings}
                                                    slotRenderEndedCb={this.stripSlotRenderEndedCb}
                                                />
                                                {element}
                                            </React.Fragment>
                                        );
                                    } else if (index === 15 && messageViewModels.length > 16) {
                                        return (
                                            <React.Fragment key={messageViewModel.message.id}>
                                                {this.squareAd('bottom')}
                                                {element}
                                            </React.Fragment>
                                        );
                                    }

                                    return element;
                                })}
                            </ul>
                        </LoadingWrapperView>
                        {thread.messages.pages > 1 ? (
                            <div key='secondPanel' className={styles.bottomControls}>
                                <Pager currentPage={thread.messages.page} pageCount={thread.messages.pages} />
                            </div>
                        ) : null}
                        {!thread.isLocked || (viewer && viewer.isSuperstaff) ? (
                            <div className={styles.newMessage} ref={this.setRef}>
                                <NewMessageView
                                    newMessageViewModel={newMessageViewModel}
                                    focus={this.state.focus}
                                    onBlur={() => this.setState({ focus: false })}
                                />
                            </div>
                        ) : null}
                        {thread.isLocked ? (
                            <p className={styles.locked}>
                                <FormattedMessage id='THREAD_WAS_LOCKED_BY_ADMIN_YOU_CANNOT_ADD_NEW_REPLIES' />
                            </p>
                        ) : null}
                    </div>
                )}
            </ViewerConsumer>
        );
    }
}

export default ThreadMessagesView;
